// TopBarSearch — real ticker search wired into the SecurityDetailModal.
//
// Replaces the prior decorative ⌘K affordance. Typing 1+ characters
// runs a debounced searchSymbols() against Polygon; results render in
// a popover and clicking one calls onPick(ticker) (Layout maps that to
// the existing setSelectedTicker portal). ⌘K / Ctrl+K from anywhere
// focuses the input. Esc clears + blurs.

import { useState, useEffect, useRef, useCallback } from 'react';
import { Search as SearchIcon, X } from 'lucide-react';
import { searchSymbols } from '../../utils/polygonService';

export default function TopBarSearch({ onPick }) {
  const [q, setQ]               = useState('');
  const [results, setResults]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [open, setOpen]         = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const wrapRef  = useRef(null);
  const debounceRef = useRef(null);

  // Global ⌘K / Ctrl+K focuses the input.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Debounced search.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults([]); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await searchSymbols(q);
        setResults(r || []);
        setActiveIdx(0);
      } catch { setResults([]); }
      setLoading(false);
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const pick = useCallback((ticker) => {
    if (!ticker) return;
    onPick?.(ticker);
    setQ(''); setResults([]); setOpen(false);
    inputRef.current?.blur();
  }, [onPick]);

  const handleKey = (e) => {
    if (e.key === 'Escape') { setQ(''); setOpen(false); inputRef.current?.blur(); return; }
    if (!open || results.length === 0) {
      if (e.key === 'Enter' && q.trim().length >= 1) pick(q.trim().toUpperCase());
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp')  { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter')    { e.preventDefault(); pick(results[activeIdx]?.ticker); }
  };

  return (
    <div ref={wrapRef} className="topnav-search" style={{ position: 'relative', flexShrink: 0, width: 240 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '7px 10px 7px 12px', borderRadius: 'var(--r-md)',
        background: 'var(--bg-surface)',
        border: `1px solid ${open ? 'var(--border-strong)' : 'var(--border-subtle)'}`,
      }}>
        <SearchIcon size={14} color="var(--text-muted)" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKey}
          placeholder="Search ticker…"
          aria-label="Search ticker"
          style={{
            flex: 1, minWidth: 0, border: 'none', background: 'transparent',
            color: 'var(--text-primary)', fontSize: 12.5, fontFamily: 'inherit',
            outline: 'none', padding: 0,
          }}
        />
        {q ? (
          <button
            onClick={() => { setQ(''); setResults([]); inputRef.current?.focus(); }}
            aria-label="Clear search"
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              color: 'var(--text-muted)', padding: 0, display: 'flex', alignItems: 'center',
            }}
          >
            <X size={13} />
          </button>
        ) : (
          <span style={{
            fontSize: 10, fontWeight: 700,
            padding: '1px 5px', borderRadius: 4,
            border: '1px solid var(--border)', color: 'var(--text-faint)',
          }}>⌘K</span>
        )}
      </div>

      {open && (q.trim() || loading) && (
        <div role="listbox" style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)',
          borderRadius: 'var(--r-md)', boxShadow: 'var(--shadow-lift)',
          maxHeight: 360, overflow: 'auto', zIndex: 300,
          animation: 'fadeIn var(--dur-fast) var(--ease-out) both',
        }}>
          {loading && (
            <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-muted)' }}>Searching…</div>
          )}
          {!loading && results.length === 0 && q.trim() && (
            <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-muted)' }}>
              No matches. Press Enter to open <b style={{ color: 'var(--text-secondary)' }}>{q.trim().toUpperCase()}</b> anyway.
            </div>
          )}
          {!loading && results.map((r, i) => {
            const active = i === activeIdx;
            return (
              <button
                key={r.ticker}
                role="option"
                aria-selected={active}
                onMouseEnter={() => setActiveIdx(i)}
                onMouseDown={(e) => { e.preventDefault(); pick(r.ticker); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', padding: '9px 12px', textAlign: 'left',
                  border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  background: active ? 'var(--accent-dim)' : 'transparent',
                  color: 'var(--text-primary)',
                }}
              >
                <span className="mono" style={{ fontWeight: 700, fontSize: 12.5, minWidth: 60 }}>{r.ticker}</span>
                <span style={{
                  fontSize: 11.5, color: 'var(--text-secondary)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0,
                }}>{r.name || '—'}</span>
                {r.exchange && (
                  <span style={{
                    fontSize: 9.5, fontWeight: 700, color: 'var(--text-faint)',
                    padding: '1px 6px', borderRadius: 4,
                    border: '1px solid var(--border)',
                  }}>{r.exchange}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
